<script setup>
import { computed, onMounted, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { format, differenceInDays } from 'date-fns';
import sessionStorage from 'shared/helpers/sessionStorage';
import AccountAPI from 'dashboard/api/account';

import BillingMeter from './components/BillingMeter.vue';
import SettingsCard from '../components/SettingsCard.vue';
import SettingsCardHeader from '../components/SettingsCardHeader.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';

const { currentAccount } = useAccount();
const { t } = useI18n();

const uiFlags = useMapGetter('accounts/getUIFlags');
const store = useStore();
const router = useRouter();
const { accountId } = useAccount();

const TRIAL_PERIOD_DAYS = 15;

const BILLING_REFRESH_ATTEMPTED = 'billing_refresh_attempted';

const isWaitingForBilling = ref(false);
const limits = ref(null);
const isFetchingLimits = ref(false);
const isTogglingOverage = ref(false);
const invoices = ref([]);
const isFetchingInvoices = ref(false);
const availableAddons = ref([]);
const togglingAddon = ref(null);
const availablePlans = ref([]);
const changingPlan = ref(null);
const paymentMethod = ref(null);
const billingInfo = ref(null);

const customAttributes = computed(() => {
  return currentAccount.value.custom_attributes || {};
});

const termsAccepted = computed(
  () =>
    !!customAttributes.value.terms_accepted_at &&
    !!customAttributes.value.dpa_accepted_at
);

const subscriptionStatus = computed(
  () => customAttributes.value.subscription_status
);

const isTrialing = computed(() => subscriptionStatus.value === 'trialing');

const trialDaysRemaining = computed(() => {
  const account = currentAccount.value;
  if (!account?.created_at) return 0;

  const elapsed = differenceInDays(new Date(), new Date(account.created_at));
  return Math.max(TRIAL_PERIOD_DAYS - elapsed, 0);
});

const planName = computed(() => {
  return customAttributes.value.plan_name;
});

const subscriptionRenewsOn = computed(() => {
  if (!customAttributes.value.subscription_ends_on) return '';
  const endDate = new Date(customAttributes.value.subscription_ends_on);
  return format(endDate, 'dd MMM, yyyy');
});

const hasABillingPlan = computed(() => {
  return !!planName.value;
});

const botConversations = computed(() => limits.value?.bot_conversations);
const nonEmailInboxes = computed(() => limits.value?.non_email_inboxes);
const emailInboxes = computed(() => limits.value?.email_inboxes);
const labelLimits = computed(() => limits.value?.labels);
const overageEnabled = computed(() => limits.value?.overage_enabled ?? false);
const overageCount = computed(() => botConversations.value?.overage ?? 0);
const overageCap = computed(() => limits.value?.overage_cap ?? 0);

const fetchLimits = async () => {
  isFetchingLimits.value = true;
  try {
    const response = await store.dispatch('accounts/limits');
    if (response?.data?.limits) {
      limits.value = response.data.limits;
    }
  } catch {
    // silent
  } finally {
    isFetchingLimits.value = false;
  }
};

const toggleOverage = async () => {
  isTogglingOverage.value = true;
  try {
    const newState = !overageEnabled.value;
    await AccountAPI.toggleOverage(newState);
    useAlert(
      newState
        ? t('BILLING_SETTINGS.OVERAGE.ENABLED_SUCCESS')
        : t('BILLING_SETTINGS.OVERAGE.DISABLED_SUCCESS')
    );
    await fetchLimits();
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      t('BILLING_SETTINGS.OVERAGE.TOGGLE_ERROR');
    useAlert(msg);
  } finally {
    isTogglingOverage.value = false;
  }
};

const fetchInvoices = async () => {
  isFetchingInvoices.value = true;
  try {
    const response = await AccountAPI.getInvoices();
    invoices.value = response.data?.invoices ?? [];
  } catch {
    // silent
  } finally {
    isFetchingInvoices.value = false;
  }
};

const formatInvoiceDate = timestamp => {
  if (!timestamp) return '';
  return format(new Date(timestamp * 1000), 'dd MMM yyyy');
};

const formatCurrency = (amount, currency) => {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currency || 'eur',
  }).format(amount / 100);
};

const fetchAvailableAddons = async () => {
  try {
    const response = await AccountAPI.getAvailableAddons();
    availableAddons.value = response.data?.addons ?? [];
  } catch {
    // silent
  }
};

const fetchAvailablePlans = async () => {
  try {
    const response = await AccountAPI.getAvailablePlans();
    availablePlans.value = response.data?.plans ?? [];
  } catch {
    // silent
  }
};

const fetchPaymentMethod = async () => {
  try {
    const response = await AccountAPI.getPaymentMethod();
    paymentMethod.value = response.data?.payment_method ?? null;
  } catch {
    // silent
  }
};

const fetchBillingInfo = async () => {
  try {
    const response = await AccountAPI.getBillingInfo();
    billingInfo.value = response.data?.billing_info ?? null;
  } catch {
    // silent
  }
};

const changePlan = async plan => {
  if (plan.current) return;
  changingPlan.value = plan.name;
  try {
    await AccountAPI.changePlan(plan.name);
    useAlert(t('BILLING_SETTINGS.PLANS.CHANGE_SUCCESS', { plan: plan.name }));
    await Promise.all([fetchAvailablePlans(), fetchLimits()]);
    // Reload account data so plan name updates globally (silent to avoid loading flash)
    await store.dispatch('accounts/get', { silent: true });
  } catch (error) {
    const msg =
      error?.response?.data?.error || t('BILLING_SETTINGS.PLANS.CHANGE_ERROR');
    useAlert(msg);
  } finally {
    changingPlan.value = null;
  }
};

const paymentMethodLabel = computed(() => {
  const pm = paymentMethod.value;
  if (!pm) return null;
  if (pm.card) return `${pm.card.brand} •••• ${pm.card.last4}`;
  if (pm.sepa_debit) return `SEPA •••• ${pm.sepa_debit.last4}`;
  return pm.type;
});

const billingAddress = computed(() => {
  const info = billingInfo.value;
  if (!info?.address) return null;
  const a = info.address;
  const parts = [
    a.line1,
    a.line2,
    [a.postal_code, a.city].filter(Boolean).join(' '),
    a.state,
    a.country,
  ].filter(Boolean);
  return parts.join(', ');
});

const toggleAddon = async (addon, quantity = null) => {
  togglingAddon.value = addon.addon_type;
  try {
    const enabling = quantity !== null ? quantity > 0 : !addon.active;
    const qty = quantity !== null ? quantity : 1;
    await AccountAPI.toggleAddon(addon.addon_type, enabling, qty);
    useAlert(
      enabling
        ? t('BILLING_SETTINGS.ADDONS.ADDED_SUCCESS')
        : t('BILLING_SETTINGS.ADDONS.REMOVED_SUCCESS')
    );
    await Promise.all([fetchAvailableAddons(), fetchLimits()]);
  } catch (error) {
    const msg =
      error?.response?.data?.error || t('BILLING_SETTINGS.ADDONS.TOGGLE_ERROR');
    useAlert(msg);
  } finally {
    togglingAddon.value = null;
  }
};

const updateAddonQuantity = async (addon, delta) => {
  const newQty = Math.max(0, (addon.quantity || 0) + delta);
  if (newQty === 0) {
    toggleAddon(addon, 0);
  } else {
    toggleAddon(addon, newQty);
  }
};

const fetchAccountDetails = async () => {
  if (!hasABillingPlan.value) {
    await store.dispatch('accounts/subscription');
  }
  fetchLimits();
  fetchInvoices();
  fetchAvailableAddons();
  fetchAvailablePlans();
  fetchPaymentMethod();
  fetchBillingInfo();
};

const handleBillingPageLogic = async () => {
  const billingRefreshAttempted = sessionStorage.get(BILLING_REFRESH_ATTEMPTED);

  await fetchAccountDetails();

  if (!hasABillingPlan.value) {
    if (!billingRefreshAttempted) {
      isWaitingForBilling.value = true;
      sessionStorage.set(BILLING_REFRESH_ATTEMPTED, true);
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } else {
      sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
    }
  } else {
    sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
  }
};

const onClickBillingPortal = () => {
  store.dispatch('accounts/checkout');
};

const onToggleChatWindow = () => {
  if (window.$rookoo) {
    window.$rookoo.toggle();
  }
};

onMounted(async () => {
  await handleBillingPageLogic();
  if (hasABillingPlan.value && !termsAccepted.value) {
    router.replace({
      name: 'billing_settings_terms',
      params: { accountId: accountId.value },
    });
  }
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetchingItem || isWaitingForBilling"
    :loading-message="
      isWaitingForBilling
        ? $t('BILLING_SETTINGS.NO_BILLING_USER')
        : $t('ATTRIBUTES_MGMT.LOADING')
    "
    :no-records-found="false"
    :no-records-message="$t('BILLING_SETTINGS.NO_BILLING_USER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TITLE')"
        :description="$t('BILLING_SETTINGS.DESCRIPTION')"
        :link-text="$t('BILLING_SETTINGS.VIEW_PRICING')"
        feature-name="billing"
      >
        <template #actions>
          <ButtonV4
            sm
            solid
            blue
            icon="i-lucide-mail"
            @click="onToggleChatWindow"
          >
            {{ $t('BILLING_SETTINGS.CONTACT_SALES') }}
          </ButtonV4>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <section class="grid gap-4">
        <div
          v-if="isTrialing"
          class="flex items-center gap-3 rounded-xl border px-5 py-4"
          :class="
            trialDaysRemaining > 3
              ? 'border-n-brand bg-n-alpha-1'
              : 'border-n-ruby bg-r-alpha-1'
          "
        >
          <span class="i-lucide-clock text-lg" />
          <div class="text-sm text-n-slate-12">
            {{
              $t('BILLING_SETTINGS.TRIAL.MESSAGE', {
                days: trialDaysRemaining,
              })
            }}
          </div>
        </div>
        <SettingsCard
          :title="$t('BILLING_SETTINGS.PLANS.TITLE')"
          :description="$t('BILLING_SETTINGS.PLANS.DESCRIPTION')"
        >
          <div
            v-if="availablePlans.length > 0"
            class="grid gap-4 px-5 py-4 sm:grid-cols-3"
          >
            <div
              v-for="plan in availablePlans"
              :key="plan.name"
              class="flex flex-col rounded-xl border p-4"
              :class="
                plan.current ? 'border-n-brand bg-n-alpha-1' : 'border-n-weak'
              "
            >
              <div class="flex items-center justify-between mb-1">
                <span class="text-sm font-semibold capitalize text-n-slate-12">
                  {{ plan.name }}
                </span>
                <span
                  v-if="plan.current"
                  class="inline-flex items-center rounded-full bg-n-slate-3 px-2 py-0.5 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('BILLING_SETTINGS.PLANS.CURRENT') }}
                </span>
              </div>
              <p
                v-if="plan.description"
                class="mb-3 text-xs leading-relaxed text-n-slate-10"
              >
                {{ plan.description }}
              </p>
              <div v-if="plan.price_label" class="mb-3">
                <span class="text-lg font-bold text-n-slate-12">{{
                  plan.price_label
                }}</span>
              </div>
              <ul
                v-if="plan.features?.length"
                class="mb-4 flex flex-col gap-1.5"
              >
                <li
                  v-for="feature in plan.features"
                  :key="feature.label"
                  class="flex items-center justify-between text-xs"
                >
                  <span class="text-n-slate-10">{{ feature.label }}</span>
                  <span class="font-medium text-n-slate-12">{{
                    feature.value
                  }}</span>
                </li>
              </ul>
              <div class="mt-auto">
                <ButtonV4
                  xs
                  class="w-full"
                  :solid="!plan.current"
                  :flushed="plan.current"
                  :blue="!plan.current"
                  :slate="plan.current"
                  :disabled="plan.current"
                  :is-loading="changingPlan === plan.name"
                  @click="changePlan(plan)"
                >
                  {{
                    plan.current
                      ? $t('BILLING_SETTINGS.PLANS.CURRENT_PLAN')
                      : $t('BILLING_SETTINGS.PLANS.SWITCH')
                  }}
                </ButtonV4>
              </div>
            </div>
          </div>
          <div
            v-if="subscriptionRenewsOn"
            class="px-5 pb-3 text-xs text-n-slate-10"
          >
            {{ $t('BILLING_SETTINGS.CURRENT_PLAN.RENEWS_ON') }}:
            {{ subscriptionRenewsOn }}
          </div>
        </SettingsCard>

        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <SettingsCard
            :title="$t('BILLING_SETTINGS.PAYMENT_METHOD.TITLE')"
            :description="$t('BILLING_SETTINGS.PAYMENT_METHOD.DESCRIPTION')"
          >
            <template #action>
              <ButtonV4 sm solid blue @click="onClickBillingPortal">
                {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.MANAGE') }}
              </ButtonV4>
            </template>
            <div v-if="paymentMethodLabel" class="px-5 pb-4">
              <div class="flex items-center gap-2 text-sm text-n-slate-12">
                <span class="i-lucide-credit-card text-n-slate-10" />
                {{ paymentMethodLabel }}
              </div>
            </div>
            <div v-else class="px-5 pb-4 text-sm text-n-slate-11">
              {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.NONE') }}
            </div>
          </SettingsCard>

          <SettingsCard
            :title="$t('BILLING_SETTINGS.BILLING_INFO.TITLE')"
            :description="$t('BILLING_SETTINGS.BILLING_INFO.DESCRIPTION')"
          >
            <template #action>
              <ButtonV4 sm solid blue @click="onClickBillingPortal">
                {{ $t('BILLING_SETTINGS.BILLING_INFO.MANAGE') }}
              </ButtonV4>
            </template>
            <div class="px-5 pb-4 grid gap-2 text-sm">
              <div v-if="billingInfo?.name" class="flex gap-2">
                <span class="text-n-slate-10 min-w-[5rem]">
                  {{ $t('BILLING_SETTINGS.BILLING_INFO.NAME') }}
                </span>
                <span class="text-n-slate-12">{{ billingInfo.name }}</span>
              </div>
              <div v-if="billingInfo?.email" class="flex gap-2">
                <span class="text-n-slate-10 min-w-[5rem]">
                  {{ $t('BILLING_SETTINGS.BILLING_INFO.EMAIL') }}
                </span>
                <span class="text-n-slate-12">{{ billingInfo.email }}</span>
              </div>
              <div v-if="billingInfo?.phone" class="flex gap-2">
                <span class="text-n-slate-10 min-w-[5rem]">
                  {{ $t('BILLING_SETTINGS.BILLING_INFO.PHONE') }}
                </span>
                <span class="text-n-slate-12">{{ billingInfo.phone }}</span>
              </div>
              <div v-if="billingAddress" class="flex gap-2">
                <span class="text-n-slate-10 min-w-[5rem]">
                  {{ $t('BILLING_SETTINGS.BILLING_INFO.ADDRESS') }}
                </span>
                <span class="text-n-slate-12">{{ billingAddress }}</span>
              </div>
              <div v-if="billingInfo?.tax_ids?.length" class="flex gap-2">
                <span class="text-n-slate-10 min-w-[5rem]">
                  {{ $t('BILLING_SETTINGS.BILLING_INFO.TAX_ID') }}
                </span>
                <span class="text-n-slate-12">
                  {{ billingInfo.tax_ids.map(t => t.value).join(', ') }}
                </span>
              </div>
              <div v-if="!billingInfo" class="text-sm text-n-slate-11">
                {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.NONE') }}
              </div>
            </div>
          </SettingsCard>
        </div>

        <SettingsCard
          :title="$t('BILLING_SETTINGS.USAGE.TITLE')"
          :description="$t('BILLING_SETTINGS.USAGE.DESCRIPTION')"
        >
          <template #action>
            <ButtonV4
              sm
              flushed
              slate
              icon="i-lucide-refresh-cw"
              :is-loading="isFetchingLimits"
              @click="fetchLimits"
            >
              {{ $t('BILLING_SETTINGS.USAGE.REFRESH') }}
            </ButtonV4>
          </template>
          <div v-if="botConversations" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.BOT_CONVERSATIONS')"
              :consumed="botConversations.consumed"
              :total-count="botConversations.allowed"
            />
          </div>
          <div v-if="nonEmailInboxes" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.NON_EMAIL_INBOXES')"
              :consumed="nonEmailInboxes.consumed"
              :total-count="nonEmailInboxes.allowed"
            />
          </div>
          <div v-if="emailInboxes" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.EMAIL_INBOXES')"
              :consumed="emailInboxes.consumed"
              :total-count="emailInboxes.allowed"
            />
          </div>
          <div v-if="labelLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.LABELS')"
              :consumed="labelLimits.consumed"
              :total-count="labelLimits.allowed"
            />
          </div>
        </SettingsCard>

        <SettingsCard
          :title="$t('BILLING_SETTINGS.OVERAGE.TITLE')"
          :description="$t('BILLING_SETTINGS.OVERAGE.DESCRIPTION')"
        >
          <template #action>
            <ButtonV4
              sm
              :solid="!overageEnabled"
              :flushed="overageEnabled"
              :blue="!overageEnabled"
              :slate="overageEnabled"
              :is-loading="isTogglingOverage"
              @click="toggleOverage"
            >
              {{
                overageEnabled
                  ? $t('BILLING_SETTINGS.OVERAGE.DISABLE')
                  : $t('BILLING_SETTINGS.OVERAGE.ENABLE')
              }}
            </ButtonV4>
          </template>
          <div class="px-5 py-3 text-sm text-n-slate-11">
            <p v-if="overageEnabled">
              {{ $t('BILLING_SETTINGS.OVERAGE.ENABLED_INFO') }}
            </p>
            <p v-else>
              {{ $t('BILLING_SETTINGS.OVERAGE.DISABLED_INFO') }}
            </p>
          </div>
          <div v-if="overageEnabled && overageCap > 0" class="px-5 pb-4">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.OVERAGE.METER_TITLE')"
              :consumed="overageCount"
              :total-count="overageCap"
            />
          </div>
          <div
            v-else-if="overageEnabled && overageCount > 0"
            class="px-5 pb-4 text-xs text-n-slate-11"
          >
            {{
              $t('BILLING_SETTINGS.OVERAGE.CONVERSATIONS_USED', {
                count: overageCount,
              })
            }}
          </div>
        </SettingsCard>

        <SettingsCard
          :title="$t('BILLING_SETTINGS.ADDONS.TITLE')"
          :description="$t('BILLING_SETTINGS.ADDONS.DESCRIPTION')"
        >
          <div v-if="availableAddons.length > 0" class="px-5 py-3">
            <div
              v-for="addon in availableAddons"
              :key="addon.addon_type"
              class="flex items-center justify-between py-2.5 border-b border-n-weak last:border-0"
            >
              <div>
                <span class="text-sm text-n-slate-12">
                  {{
                    $t(
                      `BILLING_SETTINGS.ADDONS.TYPES.${addon.addon_type.toUpperCase()}`,
                      addon.name
                    )
                  }}
                </span>
                <span
                  v-if="addon.price_label"
                  class="ml-2 text-xs text-n-slate-10"
                >
                  {{ addon.price_label
                  }}{{
                    addon.supports_quantity
                      ? ` ${$t('BILLING_SETTINGS.ADDONS.EACH')}`
                      : ''
                  }}
                </span>
              </div>
              <div
                v-if="addon.supports_quantity"
                class="flex items-center gap-2"
              >
                <ButtonV4
                  xs
                  flushed
                  slate
                  :disabled="
                    !addon.active || togglingAddon === addon.addon_type
                  "
                  :is-loading="togglingAddon === addon.addon_type"
                  @click="updateAddonQuantity(addon, -1)"
                >
                  −
                </ButtonV4>
                <span
                  class="min-w-[1.5rem] text-center text-sm font-medium tabular-nums text-n-slate-12"
                >
                  {{ addon.quantity || 0 }}
                </span>
                <ButtonV4
                  xs
                  flushed
                  slate
                  :disabled="togglingAddon === addon.addon_type"
                  :is-loading="togglingAddon === addon.addon_type"
                  @click="updateAddonQuantity(addon, 1)"
                >
                  +
                </ButtonV4>
              </div>
              <ButtonV4
                v-else
                xs
                :solid="!addon.active"
                :flushed="addon.active"
                :blue="!addon.active"
                :slate="addon.active"
                :is-loading="togglingAddon === addon.addon_type"
                @click="toggleAddon(addon)"
              >
                {{
                  addon.active
                    ? $t('BILLING_SETTINGS.ADDONS.REMOVE')
                    : $t('BILLING_SETTINGS.ADDONS.ADD')
                }}
              </ButtonV4>
            </div>
          </div>
        </SettingsCard>

        <SettingsCard
          :title="$t('BILLING_SETTINGS.INVOICES.TITLE')"
          :description="$t('BILLING_SETTINGS.INVOICES.DESCRIPTION')"
        >
          <template #action>
            <ButtonV4
              sm
              flushed
              slate
              icon="i-lucide-refresh-cw"
              :is-loading="isFetchingInvoices"
              @click="fetchInvoices"
            >
              {{ $t('BILLING_SETTINGS.USAGE.REFRESH') }}
            </ButtonV4>
          </template>
          <div v-if="invoices.length > 0" class="px-5 pb-4">
            <table class="w-full text-sm">
              <thead>
                <tr
                  class="border-b border-n-weak text-left text-xs uppercase tracking-wider text-n-slate-10"
                >
                  <th class="pb-2 pr-4 font-medium">
                    {{ $t('BILLING_SETTINGS.INVOICES.NUMBER') }}
                  </th>
                  <th class="pb-2 pr-4 font-medium">
                    {{ $t('BILLING_SETTINGS.INVOICES.DATE') }}
                  </th>
                  <th class="pb-2 pr-4 font-medium">
                    {{ $t('BILLING_SETTINGS.INVOICES.AMOUNT') }}
                  </th>
                  <th class="pb-2 pr-4 font-medium">
                    {{ $t('BILLING_SETTINGS.INVOICES.STATUS') }}
                  </th>
                  <th class="pb-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="invoice in invoices"
                  :key="invoice.id"
                  class="border-b border-n-weak last:border-0"
                >
                  <td class="py-2.5 pr-4 text-n-slate-12">
                    {{ invoice.number }}
                  </td>
                  <td class="py-2.5 pr-4 text-n-slate-11">
                    {{ formatInvoiceDate(invoice.created) }}
                  </td>
                  <td class="py-2.5 pr-4 tabular-nums text-n-slate-12">
                    {{
                      formatCurrency(
                        invoice.amount_paid || invoice.amount_due,
                        invoice.currency
                      )
                    }}
                  </td>
                  <td class="py-2.5 pr-4">
                    <span
                      class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                      :class="{
                        'bg-n-teal-3 text-n-teal-11': invoice.status === 'paid',
                        'bg-n-amber-3 text-n-amber-11':
                          invoice.status === 'open',
                        'bg-n-slate-3 text-n-slate-11':
                          invoice.status === 'draft',
                        'bg-n-ruby-3 text-n-ruby-11':
                          invoice.status === 'uncollectible',
                      }"
                    >
                      {{ invoice.status }}
                    </span>
                  </td>
                  <td class="py-2.5 text-right">
                    <a
                      v-if="invoice.hosted_invoice_url"
                      :href="invoice.hosted_invoice_url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-n-blue-11 hover:text-n-blue-12 text-xs font-medium"
                    >
                      {{ $t('BILLING_SETTINGS.INVOICES.VIEW') }}
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-else-if="!isFetchingInvoices"
            class="px-5 pb-4 text-sm text-n-slate-11"
          >
            {{ $t('BILLING_SETTINGS.INVOICES.EMPTY') }}
          </div>
        </SettingsCard>

        <SettingsCardHeader
          class="px-1 mt-5"
          :title="$t('BILLING_SETTINGS.CHAT_WITH_US.TITLE')"
          :description="$t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION')"
        >
          <ButtonV4
            sm
            solid
            slate
            icon="i-lucide-life-buoy"
            @click="onToggleChatWindow"
          >
            {{ $t('BILLING_SETTINGS.CHAT_WITH_US.BUTTON_TXT') }}
          </ButtonV4>
        </SettingsCardHeader>
      </section>
    </template>
  </SettingsLayout>
</template>
